import React, { useState, useEffect } from "react";
import {
  Card,
  Col,
  Container,
  Row,
  Button,
  Table,
  Image,
  Modal,
  Form,
  Badge,
} from "react-bootstrap";
import BreadcrumbSection from "../../../components/BreadcrumbSection";
import { Icon } from "@iconify/react";
import IMAGES from "../../../constants/images";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { getProductList } from "api/ActivityService"; // Assuming you have this API function
import RatingStars from "components/RatingStars";
import CollapsibleText from "components/CollapsibleText";

const data = [
  {
    category: "Project Management",
    score1: 9.0,
    count1: 1036,
    score2: 8.9,
    count2: 1234,
    score3: 4.9,
    count3: 1034,
  },
  {
    category: "Project and Portfolio Management",
    score1: 9.5,
    count1: 1245,
    score2: 9.1,
    count2: 1678,
    score3: 9.1,
    count3: 1678,
  },
  {
    category: "Business Instant Messaging",
    score1: 8.7,
    count1: 1526,
    score2: 8.9,
    count2: 1345,
    score3: 8.8,
    count3: 1305,
  },
  {
    category: "Workflow Management",
    score1: 8.9,
    count1: 456,
    score2: "No Data Found",
    count2: "No Data Found",
    score3: "No Data Found",
    count3: "No Data Found",
  },
  {
    category: "Objectives and Key Results (OKR)",
    score1: "No Data Found",
    count1: "No Data Found",
    score2: 8.7,
    count2: 1456,
    score3: 8.9,
    count3: 1336,
  },
  {
    category: "Kanban Project Management",
    score1: 8.0,
    count1: 1678,
    score2: 9.5,
    count2: 1478,
    score3: 9.5,
    count3: 1478,
  },
  {
    category: "Privacy Impact Assessment (PIA)",
    score1: 8.5,
    count1: 1324,
    score2: 8.3,
    count2: 1789,
    score3: 8.3,
    count3: 1789,
  },
  {
    category: "Marketing Calendar",
    score1: 9.7,
    count1: 1342,
    score2: "No Data Found",
    count2: "No Data Found",
    score3: "No Data Found",
    count3: "No Data Found",
  },
  {
    category: "Project Collaboration",
    score1: 9.0,
    count1: 1782,
    score2: 8.9,
    count2: 1234,
    score3: 8.5,
    count3: 1234,
  },
  {
    category: "Document Creation",
    score1: 9.2,
    count1: 1627,
    score2: 8.0,
    count2: 1789,
    score3: 8.1,
    count3: 1709,
  },
  {
    category: "Markup Software",
    score1: "No Data Found",
    count1: "No Data Found",
    score2: 8.5,
    count2: 1120,
    score3: "No Data Found",
    count3: "No Data Found",
  },
  {
    category: "Time Tracking",
    score1: "No Data Found",
    count1: "No Data Found",
    score2: 8.9,
    count2: 1234,
    score3: "No Data Found",
    count3: "No Data Found",
  },
];

const ProductComparison = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [featureCategories, setFeatureCategories] = useState([
    "Project Management",
    "Project and Portfolio Management",
    "Business Instant Messaging",
    "Workflow Management",
    "Objectives and Key Results (OKR)",
    "Kanban Project Management",
    "Privacy Impact Assessment (PIA)",
    "Marketing Calendar",
    "Project Collaboration",
    "Document Creation",
    "Markup Software",
    "Time Tracking",
  ]);

  // Get products from location state or localStorage
  useEffect(() => {
    // Try to get products from location state
    const productsFromState = location.state?.products || [];

    // If products exist in state, use them
    if (productsFromState.length > 0) {
      setSelectedProducts(productsFromState);
      // Also save to localStorage
      localStorage.setItem(
        "comparisonProducts",
        JSON.stringify(productsFromState)
      );
    } else {
      // Otherwise try to get from localStorage
      const savedProducts =
        JSON.parse(localStorage.getItem("comparisonProducts")) || [];
      setSelectedProducts(savedProducts);
    }

    // Fetch all products for the modal
    fetchProductList();
  }, [location]);

  const fetchProductList = async () => {
    try {
      const res = await getProductList();
      if (res.success) {
        setProducts(res.body);
      }
    } catch (err) {
      console.error("Failed to fetch products", err);
    }
  };

  const handleOpenModal = () => {
    // Only open modal if less than 3 products are selected
    if (selectedProducts.length < 3) {
      setShowModal(true);
      setSearchTerm("");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSearchTerm("");
  };

  const handleSelectProduct = (product) => {
    if (selectedProducts.length < 3) {
      // Check if product is already selected
      if (!selectedProducts.some((p) => p._id === product._id)) {
        const updatedProducts = [...selectedProducts, product];
        setSelectedProducts(updatedProducts);
        localStorage.setItem(
          "comparisonProducts",
          JSON.stringify(updatedProducts)
        );
      }
    }
    setShowModal(false);
  };

  const handleRemoveProduct = (productId) => {
    const updatedProducts = selectedProducts.filter((p) => p._id !== productId);
    setSelectedProducts(updatedProducts);
    localStorage.setItem("comparisonProducts", JSON.stringify(updatedProducts));

    // 🔁 Trigger modal if only 1 product left
    if (updatedProducts.length === 1) {
      setTimeout(() => {
        setShowModal(true);
      }, 300);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filter products based on search term
  const filteredProducts = products.filter((product) =>
    product?.name?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  // Generate comparison sections
  const renderComparisonSections = () => {
    if (selectedProducts.length < 2) {
      return (
        <div className="text-center py-5 compare_product_height">
          <h4>Select at least 2 products to compare</h4>
          <p>
            Choose products from the product listing or click below to select
            products.
          </p>
          <Button variant="primary" onClick={handleOpenModal} className="mt-3">
            Select Products to Compare
          </Button>
        </div>
      );
    }
console.log('selectedProducts', selectedProducts)
    return (
      <>
        {/* Overview Section */}
        <section className="mb-4">
          {/* <h3 className="border-bottom pb-3 mb-4">Product Overview</h3> */}
          <Row className="p-0">
            {selectedProducts.length < 3 && (
              <Col xs={4}  sm={4} md={4} className="compare_card">
                <div
                  className="com_box cursor-pointer card text-center"
                  onClick={handleOpenModal}
                  style={{ cursor: "pointer" }}
                >
                <div>
                <Button
                    variant="outline-secondary"
                    className="rounded-circle mb-3"
                    style={{ width: "80px", height: "80px" }}
                  >
                    <Icon icon="basil:add-outline" fontSize={32} />
                  </Button>
                  <h5 className="mt-3">Add Product</h5>
                </div>
                  <p className="text-muted">Compare up to 3 products</p>
                </div>
              </Col>
            )}
            {selectedProducts.map((product, idx) => (
              <Col  xs={4} sm={4} md={4} key={idx} className="compare_card">
                <div className="com_box cursor-pointer card text-center">
                  {/* Added remove button for individual products */}
                  <Button
                    variant="link"
                    className="position-absolute top-0 end-0 p-2 text-danger"
                    onClick={() => handleRemoveProduct(product._id)}
                    aria-label="Remove product"
                  >
                    <Icon icon="mdi:close" fontSize={20} />
                  </Button>
                  <div
                    className="d-inline-flex image_box align-items-center justify-content-center mb-3"
                    style={{
                      width: "120px",
                      height: "120px",
                      borderRadius: "10px",
                      backgroundColor:
                        product.brandColors || product.bgColor || "#f8f9fa",
                    }}
                  >
                    <img
                      src={product.logoUrl}
                      alt={product.name}
                      style={{
                        width: "80px",
                        height: "80px",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                  <h4>{product.name}</h4>
                  <p className="text-muted">
                    <a
                      href={product.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {product.websiteUrl?.replace("https://", "")}
                    </a>
                  </p>
                  {/* <div className="d-flex align-items-center justify-content-center">
                    <RatingStars rating={4.5} />
                    <span className="ms-2">(2,306 reviews)</span>
                  </div> */}

                  <Button
                    variant="info"
                    className="mt-3 px-4 rounded-pill"
                    as="a"
                    href={product.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: "#0071E3",
                      border: "none",
                      padding: "15px",
                      fontSize: "15px",
                      fontWeight: "500",
                    }}
                  >
                    Try For Free
                  </Button>
                </div>
              </Col>
            ))}
          </Row>
        </section>

        {/* Summary Comparison Section */}
        <section className="bg-white py-4 mb-4 rounded shadow-sm summary_section">
          <Container>
            <h3 className="border-bottom pb-3 mb-4">Summary Comparison</h3>
            <Row className="mobile_gap">
              {selectedProducts.map((product, idx) => (
                <Col md={selectedProducts.length === 3 ? 4 : 6} key={idx}>
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body>
                      <h4>{product.name}</h4>
                      <CollapsibleText lines={5}>
                        <div
                       style={{ 
                        whiteSpace: "pre-wrap",
                        lineHeight: "1.6",
                        marginBottom: "1rem"
                      }}
                          dangerouslySetInnerHTML={{
                            __html:
                              product.description ||
                              "<p class='text-muted'>No description available</p>",
                          }}
                        />
                      </CollapsibleText>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Review and Ratings Section */}
        <div className="table_card">
          <Table bordered responsive>
            <thead className="bg-light">
              <tr>
                <th colSpan={selectedProducts.length + 1}>
                  Review and Ratings
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ width: "30%" }}> </td>
                {selectedProducts.map((product, idx) => (
                  <td key={idx}>{product.name}</td>
                ))}
              </tr>
              <tr>
                <td>
                  <strong>Ratings</strong>
                </td>
                {selectedProducts.map((product, idx) => (
                  <td key={idx}>
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <RatingStars rating={product.avgRating || 0} />
                      <span>
                        {(product.avgRating || 0).toFixed(1)} out of 5
                      </span>
                    </div>
                  </td>
                ))}
              </tr>
              <tr>
                <td>
                  <strong>Review Counts</strong>
                </td>
                {selectedProducts.map((product, idx) => (
                  <td key={idx}>{product.totalReviews || 0}</td>
                ))}
              </tr>
            </tbody>
          </Table>
        </div>

        {/* Pricing Section */}
        <div className="table_card pricing_Box">
          <Table bordered responsive>
            <thead>
              <tr>
                <th colSpan={selectedProducts.length + 1}>Pricing</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ width: "30%" }}>Entry Level Pricing</td>
                {selectedProducts.map((product, idx) =>{
                  const pricing = product?.pricing?.find(p =>Number(p.price) === Math.min(...product?.pricing?.map(p => Number(p?.price)))) //get the minimum pricing plan

                  console.log('pricing', pricing)
                  const features =
                  pricing?.features?.length > 0
                    ? pricing.features.map(v => v?.value ?? v)
                    : pricing?.feature?.map(v => v?.value ?? v);
                  return (
                  <td key={idx} style={{ width: `${70 / selectedProducts.length}%`, minWidth: "300px" }}>
                    <div className="bg-white card border-0 h-100">
                      <div className="card-body h-100">
                        <div className="d-flex align-items-center mb-3">
                          <div
                            className="circle_img d-flex align-items-center justify-content-center"
                            style={{
                              backgroundColor: "rgb(207, 227, 248)",
                              width: "45px",
                              height: "45px",
                              borderRadius: "100%",
                              flexShrink: 0
                            }}
                          >
                            <img
                              src={product.logoUrl}
                              alt="logo"
                              style={{
                                width: "30px",
                                height: "30px",
                                objectFit: "contain",
                              }}
                            />
                          </div>
                          <div className="ms-3 text-start flex-grow-1">
                            <h6 className="mb-1 text-muted">
                              For {product.pricing?.[0]?.seats || "N/A"} Seats
                            </h6>
                            <h4 className="fw-bold mb-0" style={{ wordBreak: "break-word" }}>
                              {product.pricing?.[0]?.name || "N/A"}
                            </h4>
                          </div>
                        </div>
                        <p className="pricingPara text-muted" style={{ wordBreak: "break-word" }}>
                          {product.pricing?.[0]?.description || "No description"}
                        </p>
                        <h2 className="fw-bold text-dark">
                          ${product.pricing?.[0]?.price || 0}{" "}
                          <span className="fs-5 text-muted">/monthly</span>
                        </h2>
                        <h6 className="fw-bold mt-3 text-start">
                          What's included
                        </h6>
                        <ul className="list-unstyled text-start">
                          {features?.map(
                            (feat, featIdx) => (
                              <li
                                key={featIdx}
                                className="d-flex align-items-center mb-2"
                              >
                                <Icon
                                  icon="icon-park-solid:check-one"
                                  fontSize={20}
                                  color="#0071E3"
                                  style={{ flexShrink: 0 }}
                                />
                                <span className="ms-2" style={{ wordBreak: "break-word" }}>{feat}</span>
                              </li>
                            )
                          )}
                        </ul>
                        <Button
                          variant="primary"
                          className="mt-3 w-100 rounded-pill btn_height"
                          as="a"
                          href={`/product-details/${product.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            background: "#0071E3",
                            border: "none",
                            padding: "15px",
                            fontSize: "15px",
                            fontWeight: "500",
                          }}
                        >
                          Browse all {product.pricing?.length || 0} pricing plans
                        </Button>
                      </div>
                    </div>
                  </td>
                )})}
              </tr>
            </tbody>
            <tbody>
              <tr>
                <td className="fw-bold">Free Trials</td>
                {selectedProducts.map((product, idx) => (
                  <td key={idx} style={{ width: `${70 / selectedProducts.length}%`, minWidth: "300px" }}>
                    <Icon
                      icon="mingcute:check-circle-fill"
                      color="#1EB06F"
                      fontSize={24}
                      className="me-2"
                    />
                    Free Trial is available
                  </td>
                ))}
              </tr>
            </tbody>
          </Table>
        </div>

        {/* Features Comparison Section */}
        {/* <div className="table_card">
          <Table bordered responsive>
            <thead>
              <tr>
                <th colSpan={4}>Features By Category</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={index}>
                  <td style={{ width: "380px" }}>{item.category}</td>
                  <td>
                    <div className="progFlex">
                      {item.score1 !== "No Data Found" ? (
                        <>
                          <div className="progBox">
                            <CircularProgressbar
                              value={item.score1 * 10}
                              text={`${item.score1}%`}
                            />
                          </div>
                          <Image
                            src={IMAGES.Checkuser_Img}
                            alt="User Icon"
                            width={20}
                            height={20}
                            className="mx-2"
                          />
                          <span className="text-grey">{item.count1}</span>
                        </>
                      ) : (
                        <span className="text-grey">
                          <Image
                            src={IMAGES.Checkuser_Img}
                            alt="User Icon"
                            width={20}
                            height={20}
                            className="mx-2"
                          />
                          No Data Found
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="progFlex">
                      {item.score2 !== "No Data Found" ? (
                        <>
                          <div className="progBox progBox1">
                            <CircularProgressbar
                              value={item.score2 * 10}
                              text={`${item.score2}%`}
                            />
                          </div>
                          <Image
                            src={IMAGES.Checkuser_Img}
                            alt="User Icon"
                            width={20}
                            height={20}
                            className="mx-2"
                          />
                          <span className="text-grey">{item.count2}</span>
                        </>
                      ) : (
                        <span className="text-grey">
                          <Image
                            src={IMAGES.Checkuser_Img}
                            alt="User Icon"
                            width={20}
                            height={20}
                            className="mx-2"
                          />
                          No Data Found
                        </span>
                      )}
                    </div>
                  </td>
                  { selectedProducts.length===3 &&
                     <td>
                     <div className="progFlex">
                       {item.score1 !== "No Data Found" ? (
                         <>
                           <div className="progBox progBox1">
                             <CircularProgressbar
                               value={item.score1 * 10}
                               text={`${item.score1}%`}
                             />
                           </div>
                           <Image
                             src={IMAGES.Checkuser_Img}
                             alt="User Icon"
                             width={20}
                             height={20}
                             className="mx-2"
                           />
                           <span className="text-grey">{item.count2}</span>
                         </>
                       ) : (
                         <span className="text-grey">
                           <Image
                             src={IMAGES.Checkuser_Img}
                             alt="User Icon"
                             width={20}
                             height={20}
                             className="mx-2"
                           />
                           No Data Found
                         </span>
                       )}
                     </div>
                   </td>
                  }
                </tr>
              ))}
            </tbody>
          </Table>
        </div> */}

        {/* Key Features Section */}
        <section className="mb-4 key_features_section">
          <h3 className="mb-4">Key Features</h3>
          <Row className="mobile_gap">
            {selectedProducts.map((product, idx) => (
              <Col md={selectedProducts.length === 3 ? 4 : 6} key={idx}>
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Header
                    className="bg-transparent"
                    style={{ padding: "19px 14px 10px" }}
                  >
                    <h4>{product.name}</h4>
                  </Card.Header>
                  <Card.Body>
                    {product.features ? (
                      <div>
                        {product.features.map((feature, featureIdx) => {
                          console.log('feature', feature)
                          const description = Array.isArray(feature.description) 
                            ? feature.description.join(', ')
                            : feature.descriptions?.map(d => d.value).join(', ') || ''
                          return (
                            <div key={featureIdx} className="mb-4">
                              <h5 className="mb-3">{feature.title}</h5>
                              <ul className="list-unstyled">
                                <li dangerouslySetInnerHTML={{ __html: description }} />
                              </ul>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="text-muted">
                        No detailed feature information available
                      </p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </section>

        {/* Target Users & Industries Section */}
        <div className="table_card">
          <Table bordered responsive>
            <thead>
              <tr>
                <th colSpan={selectedProducts.length + 1}>
                  Target Users & Industries
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ width: "25%" }}></td>
                {selectedProducts.map((product, idx) => (
                  <td key={idx}>{product.name}</td>
                ))}
              </tr>
              <tr>
                <td>
                  <strong>Who Can Use</strong>
                </td>
                {selectedProducts.map((product, idx) => (
                  <td key={idx}>
                    {product.whoCanUse ? (
                      <div className="d-flex flex-wrap">
                        {product.whoCanUse.map((user, userIdx) => (
                          <Badge
                            key={userIdx}
                            bg="light"
                            text="dark"
                            className="me-2 mb-2 p-2"
                          >
                            {user.title || user}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted">
                        Information not available
                      </span>
                    )}
                  </td>
                ))}
              </tr>
              <tr>
                <td>
                  <strong>Industries</strong>
                </td>
                {selectedProducts.map((product, idx) => (
                  <td key={idx}>
                    {product.industries ? (
                      <div className="d-flex flex-wrap">
                        {product.industries.map((industry, industryIdx) => (
                          <Badge
                            key={industryIdx}
                            bg="light"
                            text="dark"
                            className="me-2 mb-2 p-2"
                          >
                            {industry.title || industry}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted">
                        Information not available
                      </span>
                    )}
                  </td>
                ))}
              </tr>
              <tr>
                <td>
                  <strong>Market Segment</strong>
                </td>
                {selectedProducts.map((product, idx) => (
                  <td key={idx}>
                    {product.marketSegment ? (
                      <div className="d-flex flex-wrap">
                        {product.marketSegment.map((segment, segmentIdx) => (
                          <Badge
                            key={segmentIdx}
                            bg="light"
                            text="dark"
                            className="me-2 mb-2 p-2"
                          >
                            {segment.title || segment}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted">
                        Information not available
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </Table>
        </div>
      </>
    );
  };

  return (
    <React.Fragment>
      {/* BreadCrumb Section */}
      <BreadcrumbSection
        breadcrumbs={[
          // {
          //   title: "Project Management",
          //   link: "/project-management-all-software",
          // },
          {
            title:
              selectedProducts.length > 0
                ? selectedProducts.map((p) => p.name).join("-vs-")
                : "Product Comparison",
          },
        ]}
      />

      <section className="comparion_section bg-light py-4">
        <Container>
          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center">
                <div className="software_header">
                  <h2>
                    {selectedProducts.length > 1
                      ? `Compare ${selectedProducts
                          .map((p) => p.name)
                          .join(" and ")}`
                      : "Select Products to Compare"}
                  </h2>
                </div>
                {/* Removed Clear All button as we now have individual remove buttons */}
              </div>
            </Col>
          </Row>

          {renderComparisonSections()}

          {selectedProducts.length === 0 && (
            <Row className="mb-4">
              <Col lg={12} className="text-center py-5">
                <h4>No products selected for comparison</h4>
                <p>
                  Choose products from the product listing to start comparing
                </p>
                <Button
                  variant="primary"
                  onClick={() => navigate("/product-listing")}
                  className="mt-3"
                >
                  Browse Products
                </Button>
              </Col>
            </Row>
          )}
        </Container>
      </section>

      {/* Modal for Product Selection */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Select a Product to Compare</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </Form.Group>

          <div className="mt-3">
            <Row>
              {filteredProducts?.map((product) => (
                <Col lg={6} md={6} sm={12} key={product._id} className="mb-4">
                  <Card
                    onClick={() => {
                      const alreadySelected = selectedProducts.some(
                        (p) => p._id === product._id
                      );
                      if (!alreadySelected) {
                        handleSelectProduct(product);
                      }
                    }}
                    className="cursor-pointer h-100"
                    style={{
                      opacity: selectedProducts.some(
                        (p) => p._id === product._id
                      )
                        ? 0.6
                        : 1,
                      cursor: selectedProducts.some(
                        (p) => p._id === product._id
                      )
                        ? "not-allowed"
                        : "pointer",
                    }}
                  >
                    <Card.Body
                      className="d-flex align-items-center"
                      style={{ padding: "13px 13px" }}
                    >
                      <div
                        className="iconBox me-2"
                        style={{
                          backgroundColor:
                            product.brandColors || product.bgColor || "#f0f0f0",
                          minWidth: "50px",
                          minHeight: "50px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={product.logoUrl}
                          alt={product.name}
                          style={{
                            width: 30,
                            height: 30,
                            objectFit: "contain",
                          }}
                        />
                      </div>
                      <div className="d-flex flex-column align-items-start text-left">
                        <h5
                          className="mb-1 text-left"
                          style={{ fontSize: "16px" }}
                        >
                          {product.name}
                        </h5>
                        <div>
                          <RatingStars rating={product.avgRating || 4} />{" "}
                          {product.avgRating || 0} (
                          {product.totalReviews || 0})
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default ProductComparison;
